/**
 * Hooks de notificaciones in-app (M-16 PRD).
 *
 * RLS policy `notifications_self` permite al user CRUD sobre sus propias
 * notifs. Los triggers SQL crean las notifs como SECURITY DEFINER.
 *
 * Polling: refetchInterval=30s para mantener el badge fresco sin meter
 * realtime subscription que ahora no vale la complejidad.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type NotificationKind =
  | 'booking_confirmed'
  | 'booking_cancelled'
  | 'session_reminder_24h'
  | 'session_reminder_1h'
  | 'session_completed'
  | 'new_review'
  | 'new_settlement'
  | 'event_starting'
  | 'community_reply'
  | 'community_reaction'
  | 'community_mention'
  | 'support_ticket_new'
  | 'support_ticket_reply'
  | 'system';

export interface NotificationRow {
  id: string;
  user_id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  cta_url: string | null;
  metadata: Record<string, unknown>;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

const POLL_INTERVAL_MS = 30_000;

/** Lista las notifs del user (más recientes primero). */
export function useNotifications(opts?: { limit?: number }) {
  const limit = opts?.limit ?? 50;
  return useQuery({
    queryKey: ['notifications', limit],
    queryFn: async (): Promise<NotificationRow[]> => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
    refetchInterval: POLL_INTERVAL_MS,
  });
}

// =====================================================================
// Página /dashboard/notifications — listado avanzado (búsqueda + filtros
// + paginación server-side). El popover del bell sigue usando
// useNotifications de arriba.
// =====================================================================

/**
 * Grupos de kinds para el filtro por categoría de la página.
 * Mantener sincronizado con NotificationKind.
 */
export const NOTIFICATION_CATEGORIES = {
  sessions: {
    label: 'Sesiones',
    kinds: [
      'booking_confirmed', 'booking_cancelled', 'session_reminder_24h',
      'session_reminder_1h', 'session_completed',
    ] as NotificationKind[],
  },
  community: {
    label: 'Comunidad',
    kinds: ['community_reply', 'community_reaction', 'community_mention'] as NotificationKind[],
  },
  reviews: {
    label: 'Reviews y pagos',
    kinds: ['new_review', 'new_settlement'] as NotificationKind[],
  },
  support: {
    label: 'Soporte',
    kinds: ['support_ticket_new', 'support_ticket_reply'] as NotificationKind[],
  },
  system: {
    label: 'Sistema',
    kinds: ['system', 'event_starting'] as NotificationKind[],
  },
} as const;

export type NotificationCategory = keyof typeof NOTIFICATION_CATEGORIES;

export interface NotificationsPageParams {
  page: number;          // 0-based
  pageSize?: number;
  search?: string;       // busca en title + body (ilike)
  unreadOnly?: boolean;
  category?: NotificationCategory | null;
}

export interface NotificationsPageResult {
  rows: NotificationRow[];
  total: number;         // total con filtros aplicados (para el paginador)
}

/**
 * Listado paginado server-side con búsqueda y filtros. La búsqueda escapa
 * `%`/`_`/`,` para que no rompan el patrón ilike ni el or() de PostgREST.
 */
export function useNotificationsPaged(params: NotificationsPageParams) {
  const { page, pageSize = 20, search = '', unreadOnly = false, category = null } = params;
  return useQuery({
    queryKey: ['notifications-paged', page, pageSize, search, unreadOnly, category],
    queryFn: async (): Promise<NotificationsPageResult> => {
      let q = supabase
        .from('notifications')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (unreadOnly) q = q.eq('is_read', false);
      if (category) q = q.in('kind', NOTIFICATION_CATEGORIES[category].kinds);

      const term = search.trim();
      if (term) {
        // Escapar caracteres especiales del patrón LIKE y de la sintaxis or()
        const safe = term.replace(/[%_,()]/g, (c) => `\\${c}`);
        q = q.or(`title.ilike.%${safe}%,body.ilike.%${safe}%`);
      }

      const from = page * pageSize;
      const { data, count, error } = await q.range(from, from + pageSize - 1);
      if (error) throw error;
      return { rows: (data ?? []) as NotificationRow[], total: count ?? 0 };
    },
    placeholderData: (prev) => prev, // evita parpadeo al cambiar de página
    refetchInterval: POLL_INTERVAL_MS,
  });
}

/** Elimina una notificación (RLS notifications_self permite DELETE). */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('notifications').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-paged'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

/** Count de notifs no leídas. Liviano — usado por el bell. */
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('is_read', false);
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: POLL_INTERVAL_MS,
  });
}

/** Marca una notif como leída. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-paged'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

/** Marca todas las del user como leídas. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('is_read', false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-paged'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}
